/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { LabelType } from '@airasia-phoenix/react-native/components/LabelView/types';
import { MemoizedListViewProps } from '@airasia-phoenix/react-native/components/SearchableListPageView/MemoizedListView';
import {
  getColor,
  pd,
  textStyle,
} from '@airasia-phoenix/react-native/utils/utils';
import type { Station, StationSelectionSource } from '../types';
import { groupStationsByCity, removeDuplicateStations } from './stationUtils';
import { STATION_SELECTION_SOURCE } from '../configs/constants';

export type StationListModel = {
  station: Station;
  isAllAirports: boolean;
  selectionSource?: StationSelectionSource;
};

type TranslationFn = (key: string) => string;

const basePaddingStart = pd.ref.space.spacing40.px;
const childPaddingStart = pd.ref.space.spacing80.px;
const listItemIconSize = pd.ref.space.spacing50.px;
const stationResultsTopGap = 20;
const RECENT_ID_TYPE = {
  ALL_AIRPORTS: 'allAirports',
  AIRPORT: 'airport',
} as const;

const getTrimmedValue = (value?: string) => value?.trim() ?? '';

const getPrimaryLabelFallback = (station: Station) =>
  getTrimmedValue(station.CityName) ||
  getTrimmedValue(station.StationName) ||
  getTrimmedValue(station.AirportName) ||
  station.StationCode;

const getLocationDisplayText = (
  cityName: string,
  countryName: string,
  fallbackName: string,
) => {
  const trimmedCity = getTrimmedValue(cityName);
  const trimmedCountry = getTrimmedValue(countryName);
  const trimmedFallback = getTrimmedValue(fallbackName);

  if (trimmedCity && trimmedCountry) return `${trimmedCity}, ${trimmedCountry}`;
  if (trimmedCity) return trimmedCity;
  if (trimmedCountry && trimmedFallback) {
    return `${trimmedFallback}, ${trimmedCountry}`;
  }
  if (trimmedCountry) return trimmedCountry;
  return trimmedFallback;
};

const getAirportIdSuffix = (station: Station, fallbackIndex: number) => {
  const stationCode = getTrimmedValue(station.StationCode);
  if (stationCode) return stationCode;

  const cityCode = getTrimmedValue(station.CityCode);
  const stationName = getTrimmedValue(station.StationName);
  const airportName = getTrimmedValue(station.AirportName);
  const fallbackName = cityCode || stationName || airportName || 'unknown';

  return `${fallbackName}-${fallbackIndex}`;
};

export const getStationDisplayValue = (
  station: Station | null,
  isAllAirports: boolean,
  t: TranslationFn,
) => {
  if (!station) return '';
  const displayCity = getPrimaryLabelFallback(station);
  if (isAllAirports) {
    return `${displayCity} (${t('searchSnapPage.stationField.allAirports')})`;
  }
  return `${displayCity} (${station.StationCode})`;
};

export const getHighlightText = (
  text: string,
  query: string,
  highlightStyle = textStyle(
    pd.sys.typescale.body_text_medium,
    pd.sys.colour.content_primary,
  ),
) => {
  if (!query || !text) return undefined;
  const lowerText = text.toLowerCase();
  const lowerQuery = query.toLowerCase();
  const index = lowerText.indexOf(lowerQuery);
  if (index === -1) return undefined;
  return [
    {
      spannable: text.substring(index, index + query.length),
      style: highlightStyle,
    },
  ];
};

export const buildStationSearchItems = (
  items: Station[],
  query: string,
  t: TranslationFn,
  idPrefix = '',
): MemoizedListViewProps[] => {
  const uniqueStations = removeDuplicateStations(items);
  const grouped = groupStationsByCity(uniqueStations);
  const listItems: MemoizedListViewProps[] = [];

  grouped.forEach((group) => {
    const firstRowTopGapStyle =
      listItems.length === 0 ? { marginTop: stationResultsTopGap } : undefined;

    if (group.airports.length > 1) {
      const primaryAirport = group.airports[0];
      const groupedPrimaryText = getLocationDisplayText(
        group.cityName,
        group.countryName,
        getPrimaryLabelFallback(primaryAirport),
      );
      listItems.push({
        id: `${idPrefix}city-${group.cityCode}`,
        model: {
          station: primaryAirport,
          isAllAirports: true,
          selectionSource: STATION_SELECTION_SOURCE.GENERAL,
        } as StationListModel,
        primaryText: groupedPrimaryText,
        primaryHighlightText: getHighlightText(groupedPrimaryText, query),
        secondaryText: t('searchSnapPage.stationField.allAirports'),
        primaryTextStyle: textStyle(
          pd.sys.typescale.body_text_medium,
          pd.sys.colour.content_primary,
        ),
        secondaryTextStyle: textStyle(
          pd.sys.typescale.sub_body_text,
          pd.sys.colour.content_secondary,
        ),
        startElement: {
          iconViewProps: {
            icon: pd.icon.main.communication.location_on,
            height: listItemIconSize,
            width: listItemIconSize,
            tint: getColor(pd.sys.colour.content_primary),
          },
        },
        endElement: {
          labelViewProps: {
            text: primaryAirport.StationCode,
            type: LabelType.TONED_GREY,
          },
        },
        containerStyle: firstRowTopGapStyle,
        paddingStart: basePaddingStart,
      });

      group.airports.forEach((airport) => {
        const airportLabel =
          airport.AirportName || airport.StationName || airport.CityName;
        const airportIdSuffix = getAirportIdSuffix(airport, listItems.length);
        listItems.push({
          id: `${idPrefix}airport-${airportIdSuffix}`,
          model: {
            station: airport,
            isAllAirports: false,
            selectionSource: STATION_SELECTION_SOURCE.GENERAL,
          } as StationListModel,
          primaryText: airportLabel,
          primaryHighlightText: getHighlightText(airportLabel, query),
          primaryTextStyle: textStyle(
            pd.sys.typescale.sub_body_text_medium,
            pd.sys.colour.content_primary,
          ),
          startElement: {
            iconViewProps: {
              icon: pd.icon.main.device.airplanemode_active,
              height: listItemIconSize,
              width: listItemIconSize,
              tint: getColor(pd.sys.colour.content_primary),
            },
          },
          endElement: {
            labelViewProps: {
              text: airport.StationCode,
              type: LabelType.TONED_GREY,
            },
          },
          paddingStart: childPaddingStart,
        });
      });
      return;
    }

    if (group.airports.length === 1) {
      const airport = group.airports[0];
      const airportLabel =
        airport.AirportName || airport.StationName || airport.CityName;
      const airportPrimaryText = getLocationDisplayText(
        airport.CityName,
        airport.CountryName,
        getPrimaryLabelFallback(airport),
      );
      const airportIdSuffix = getAirportIdSuffix(airport, listItems.length);
      listItems.push({
        id: `${idPrefix}airport-${airportIdSuffix}`,
        model: {
          station: airport,
          isAllAirports: false,
          selectionSource: STATION_SELECTION_SOURCE.GENERAL,
        } as StationListModel,
        primaryText: airportPrimaryText,
        primaryHighlightText: getHighlightText(airportPrimaryText, query),
        secondaryText: airportLabel,
        primaryTextStyle: textStyle(
          pd.sys.typescale.body_text_medium,
          pd.sys.colour.content_primary,
        ),
        secondaryTextStyle: textStyle(
          pd.sys.typescale.sub_body_text,
          pd.sys.colour.content_secondary,
        ),
        startElement: {
          iconViewProps: {
            icon: pd.icon.main.device.airplanemode_active,
            height: listItemIconSize,
            width: listItemIconSize,
            tint: getColor(pd.sys.colour.content_primary),
          },
        },
        endElement: {
          labelViewProps: {
            text: airport.StationCode,
            type: LabelType.TONED_GREY,
          },
        },
        containerStyle: firstRowTopGapStyle,
        paddingStart: basePaddingStart,
      });
    }
  });

  return listItems;
};

export const buildRecentItems = (
  recentStations: Array<{ station: Station; isAllAirports: boolean }>,
  t: TranslationFn,
  idPrefix = '',
): MemoizedListViewProps[] => {
  return recentStations.map((item, index) => ({
    id: `${idPrefix}recent-${item.station.StationCode}-${item.isAllAirports ? RECENT_ID_TYPE.ALL_AIRPORTS : RECENT_ID_TYPE.AIRPORT}-${index}`,
    model: {
      station: item.station,
      isAllAirports: item.isAllAirports,
      selectionSource: STATION_SELECTION_SOURCE.RECENT,
    } as StationListModel,
    primaryText: getLocationDisplayText(
      item.station.CityName,
      item.station.CountryName,
      getPrimaryLabelFallback(item.station),
    ),
    secondaryText: item.isAllAirports
      ? t('searchSnapPage.recentSearch.allAirports')
      : item.station.AirportName || item.station.StationName,
    primaryTextStyle: textStyle(
      pd.sys.typescale.sub_body_text_medium,
      pd.sys.colour.content_primary,
    ),
    secondaryTextStyle: textStyle(
      pd.sys.typescale.caption_text,
      pd.sys.colour.content_secondary,
    ),
    startElement: {
      iconViewProps: {
        icon: item.isAllAirports
          ? pd.icon.main.communication.location_on
          : pd.icon.main.device.airplanemode_active,
        height: listItemIconSize,
        width: listItemIconSize,
        tint: getColor(pd.sys.colour.content_primary),
      },
    },
    endElement: {
      labelViewProps: {
        text: item.station.StationCode,
        type: LabelType.TONED_GREY,
      },
    },
    paddingStart: basePaddingStart,
  }));
};
